'use client';

import React, { useState } from 'react';
import { Bell, Download, RefreshCw, Search, FileDown, X } from 'lucide-react';
import Link from 'next/link';
import type { SubmissionRow } from '@/app/admin-dashboard/components/AdminDashboardPageClient';

interface DashboardHeaderProps {
  onRefresh?: () => void;
  lastUpdated?: Date;
  loading?: boolean;
  submissions?: SubmissionRow[];
}

interface ExportModalProps {
  submissions: SubmissionRow[];
  onClose: () => void;
}

function ExportModal({ submissions, onClose }: ExportModalProps) {
  const programmes = Array.from(new Set(submissions.map((s) => s.programme).filter(Boolean))).sort();
  const [selectedProgramme, setSelectedProgramme] = useState<string>('all');
  const [exportFormat, setExportFormat] = useState<'csv' | 'tsv'>('csv');

  const handleExport = () => {
    const rows = selectedProgramme === 'all'
      ? submissions
      : submissions.filter((s) => s.programme === selectedProgramme);

    const headers = [
      'Full Name', 'TRN-4', 'Email Address', 'Mailing Address',
      'Telephone Number', 'Programme', 'Cohort',
      'Document Upload Status', 'Submission Status', 'Submission Date',
    ];

    const sep = exportFormat === 'csv' ? ',' : '\t';
    const escape = (val: string) =>
      exportFormat === 'csv' ? `"${(val || '').replace(/"/g, '""')}"` : (val || '');

    const lines = [
      headers.map(escape).join(sep),
      ...rows.map((s) =>
        [
          s.studentName, s.trn4, s.email, s.mailingAddress,
          s.telephone, s.programme, s.cohort,
          s.docUploadStatus, s.submissionStatus.replace(/_/g, ' '), s.submissionDate,
        ].map(escape).join(sep)
      ),
    ];

    const content = lines.join('\n');
    const mimeType = exportFormat === 'csv' ? 'text/csv' : 'text/tab-separated-values';
    const ext = exportFormat === 'csv' ? 'csv' : 'tsv';
    const suffix = selectedProgramme === 'all' ? 'All_Programmes' : selectedProgramme.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
    const fileName = `ZivaDocuSecure_${suffix}_${new Date().toISOString().slice(0, 10)}.${ext}`;

    const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
    onClose();
  };

  const previewCount = selectedProgramme === 'all'
    ? submissions.length
    : submissions.filter((s) => s.programme === selectedProgramme).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-modal w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileDown size={16} className="text-primary" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Export Student Records</h3>
              <p className="text-xs text-muted-foreground">Export by programme with full contact details</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Select Programme</label>
            <select
              value={selectedProgramme}
              onChange={(e) => setSelectedProgramme(e.target.value)}
              className="w-full px-3 py-2.5 bg-muted border border-border rounded-lg text-sm text-foreground outline-none focus:border-primary transition-colors cursor-pointer"
            >
              <option value="all">All Programmes ({submissions.length} students)</option>
              {programmes.map((p) => {
                const count = submissions.filter((s) => s.programme === p).length;
                return (
                  <option key={p} value={p}>{p} ({count} student{count !== 1 ? 's' : ''})</option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Export Format</label>
            <div className="flex gap-2">
              {(['csv', 'tsv'] as const).map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => setExportFormat(fmt)}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    exportFormat === fmt ? 'bg-primary text-white border-primary' : 'bg-muted text-muted-foreground border-border hover:border-primary/40'
                  }`}
                >
                  {fmt.toUpperCase()}
                  <span className="block text-[10px] font-normal opacity-70">
                    {fmt === 'csv' ? 'Excel / Sheets' : 'Tab-separated'}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <FileDown size={14} className="text-primary flex-shrink-0" />
            <p className="text-xs text-primary font-medium">
              {previewCount} student record{previewCount !== 1 ? 's' : ''} will be exported
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-5 border-t border-border">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-semibold text-muted-foreground bg-muted hover:bg-muted/80 border border-border rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={previewCount === 0}
            className="flex-1 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={14} />
            Export {previewCount} Record{previewCount !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardHeader({ onRefresh, lastUpdated, loading, submissions = [] }: DashboardHeaderProps) {
  const [showExportModal, setShowExportModal] = useState(false);

  const formattedTime = lastUpdated
    ? lastUpdated.toLocaleString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : '';

  return (
    <>
      {showExportModal && (
        <ExportModal submissions={submissions} onClose={() => setShowExportModal(false)} />
      )}
      <div className="sticky top-0 z-30 bg-card/95 backdrop-blur-sm border-b border-border px-6 lg:px-8 xl:px-10 2xl:px-12 py-4">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Ziva DocuSecure — Document Verification Operations
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-muted rounded-lg border border-border text-sm text-muted-foreground cursor-pointer hover:bg-muted/70 transition-colors">
              <Search size={14} />
              <span>Search students...</span>
              <kbd className="ml-2 px-1.5 py-0.5 text-[10px] font-mono bg-card border border-border rounded text-muted-foreground">
                ⌘K
              </kbd>
            </div>

            {/* Refresh */}
            <button
              onClick={onRefresh}
              disabled={loading}
              className="btn-outline px-3 py-2 gap-1.5 text-xs disabled:opacity-60"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            {/* Export */}
            <button
              onClick={() => setShowExportModal(true)}
              className="btn-outline px-3 py-2 gap-1.5 text-xs"
            >
              <Download size={13} />
              <span className="hidden sm:inline">Export</span>
            </button>

            {/* Notifications */}
            <button className="relative w-9 h-9 flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
              <Bell size={16} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary" />
            </button>

            {/* New Registration CTA */}
            <Link href="/student-registration-form" className="btn-primary px-4 py-2 text-sm hidden sm:inline-flex">
              + New Registration
            </Link>
          </div>
        </div>

        {/* Last updated bar */}
        <div className="max-w-screen-2xl mx-auto mt-2 flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${loading ? 'bg-amber-400' : 'bg-green-500 animate-pulse'}`} />
          <span className="text-xs text-muted-foreground">
            {loading ? 'Loading data...' : `Live data — last updated ${formattedTime}`}
          </span>
        </div>
      </div>
    </>
  );
}